import React, { useMemo } from 'react';
import { getScrollMapsColorForPageView, ScrollMapsColorMap } from '../utils/colormap';
import type { ScrollMapsFacet } from '../utils/facetdata';
import type { Bounds, PlaybackScrollElementBounds } from '../types';
import { createScaledOffsets, InverseScales } from '../utils/scales';
import { ScrollMapsMarker } from '../ScrollMapsMarker';
import { shortNumber } from '@fullstory/util/fmt';
import { MARKER_HEIGHT, MARKER_LINE_HEIGHT } from '../ScrollMapsMarkerContainer';
import { useIntl } from 'react-intl';

/** The default Scroll Marker percents to show. */
const SCROLL_MARKER_PERCENTS = [100, 75, 50, 25];

interface Props {
  /**
   * The map responsible for producing the Scroll Maps colors.
   */
  colorMap: ScrollMapsColorMap;
  /**
   * The current response from the network.
   */
  facetData: ScrollMapsFacet;
  /**
   * Details about the scrolling element and the player viewport.
   */
  playbackScrollElementBounds: PlaybackScrollElementBounds;
  /**
   * The bounds for the page itself
   */
  pageBounds: Bounds;
  /**
   * The inverse scales to be applied to undo any scaling applied by playback.
   */
  inverseScales: InverseScales;
  /**
   * The zIndex offset for this marker. The marker's index is pageBounds.zIndex + zIndexOffset.
   */
  zIndexOffset: number;
}

// Determine the marker box width by the number of characters that it will contain. This allows the box width to be somewhat
// dynamic so that the content fits snuggly but not so dynamic that it changes size by a couple pixels every time a
// character in the content changes.
const getMarkerWidth = (chars: number): number => {
  switch (chars) {
    case 16: // "1 pageviews (1%)"
      return 140;
    case 17: // "1 pageviews (10%)"
      return 146;
    case 18: // "1 pageviews (100%)"
      return 152;
    case 19: // "10 pageviews (100%)"
      return 162;
    case 20: // "100 pageviews (100%)"
      return 168;
    case 21: // "1050 pageviews (100%)"
      return 172;
    case 22: // "99.9K pageviews (100%)"
      return 184;
    case 23: // "999.9K pageviews (100%)"
      return 192;
    default:
      return 200;
  }
};

/**
 * Shows static `ScrollMapsMarker` at certain intervals (i.e., at 100%, 75%, 50%, and 25%) vs having a draggable
 * `ScrollMapsMarker` that the user interacts with.
 */
export const StaticScrollMapsMarkerContainer: React.FC<Props> = ({
  colorMap,
  facetData,
  playbackScrollElementBounds,
  pageBounds,
  inverseScales,
  zIndexOffset,
}) => {
  const { scrollElementTop, viewportHeight, containerHeight } = playbackScrollElementBounds;

  // Holds what part of the page the viewport is showing, with respect to the given top value. For example, if a page is
  // 4000px long, we've scrolled 1200px, and the viewport is 1000px big, this will hold { top: -1500, bottom: -2500 }.
  const viewportPagePosition = {
    top: scrollElementTop,
    bottom: scrollElementTop - viewportHeight,
  };

  const pageViewPercentToContainerPixels: { [key: number]: number } = useMemo(() => {
    const pagePercentToPageViewPercent = facetData.depthCounts.map(item =>
      Math.floor((item / facetData.totalPageViews) * 100),
    );

    const percentDownForPageViewPercents: { [key: number]: number } = {};

    // Find % scroll down for percents we want to use.
    SCROLL_MARKER_PERCENTS.forEach(desiredPageViewPercent => {
      let bestApproximationPageViewPercent = 0;
      let foundExactMatch = false;

      for (const [percentDown, pageViewPercent] of pagePercentToPageViewPercent.entries()) {
        if (pageViewPercent === desiredPageViewPercent) {
          bestApproximationPageViewPercent = percentDown;
          foundExactMatch = true;
        } else if (pageViewPercent < desiredPageViewPercent) {
          // If we found an exact match, then use that value. If we didn't, even we jumped, for example, 77% -> 66%.
          // In this case, if we're looking for the value to show for 75% of users, we're going to use the 66% value.
          if (!foundExactMatch) {
            bestApproximationPageViewPercent = percentDown;
          }
          break;
        }
      }

      percentDownForPageViewPercents[desiredPageViewPercent] = bestApproximationPageViewPercent;
    });

    const pageViewPercentToContainerPixels: { [key: number]: number } = {};
    for (const [desiredScrollPercent, pageScrollPercent] of Object.entries(
      percentDownForPageViewPercents,
    )) {
      pageViewPercentToContainerPixels[parseInt(desiredScrollPercent, 10)] =
        (pageScrollPercent / 100) * -containerHeight;
    }

    return pageViewPercentToContainerPixels;
  }, [facetData.depthCounts, facetData.totalPageViews, containerHeight]);

  const getPageViewNumber = (percent: number) =>
    Math.round((percent * facetData.totalPageViews) / 100);
  const overlayPositionForPercents: { [key: number]: number } = {};
  const onScreen: number[] = [];
  const intl = useIntl();

  // Remove entries which are duplicated. Keep the lower ones (e.g. if 50% and 75% map to the same thing, display 50% only)
  for (const percentToKeep of SCROLL_MARKER_PERCENTS) {
    for (const percentToCheck of SCROLL_MARKER_PERCENTS) {
      if (
        percentToCheck !== 100 &&
        percentToKeep < percentToCheck &&
        pageViewPercentToContainerPixels[percentToKeep] ===
          pageViewPercentToContainerPixels[percentToCheck]
      ) {
        delete pageViewPercentToContainerPixels[percentToCheck];
      }
    }
  }

  for (const percent of SCROLL_MARKER_PERCENTS) {
    const percentPagePosition = pageViewPercentToContainerPixels[percent];

    if (!percentPagePosition) {
      continue;
    }

    const pageViewText = intl.formatMessage(
      { defaultMessage: '{userCount, number, ::K .#} Users', id: 'dRmJtL' },
      { userCount: getPageViewNumber(percent) },
    );
    const markerWidth = getMarkerWidth(`${pageViewText} (${percent}%)`.length);

    const scaledOffsets = createScaledOffsets(MARKER_HEIGHT, markerWidth, inverseScales);
    // Logic copied from ts/ui/playback/slider.ts
    const yPosOffset = percentPagePosition - MARKER_LINE_HEIGHT - scaledOffsets.y;

    // Find the positions of where the marker should be in the overlay
    if (yPosOffset > viewportPagePosition.bottom && yPosOffset < viewportPagePosition.top) {
      onScreen.push(percent);

      const totalShownTop = viewportPagePosition.bottom - viewportPagePosition.top;
      const topDiff = percentPagePosition - viewportPagePosition.top;
      const ratio = topDiff / totalShownTop;

      overlayPositionForPercents[percent] = -totalShownTop * ratio - scaledOffsets.y;
    }
  }

  return (
    <>
      {onScreen.map(percent => {
        const pageViewText = intl.formatMessage(
          { defaultMessage: '{userCount, number, ::K .#} pageviews', id: 'cTMegw' },
          { userCount: getPageViewNumber(percent) },
        );
        const markerWidth = getMarkerWidth(`${pageViewText} (${percent})%`.length);

        // Place the top such that the center of the marker is at position. The center of the marker remains the same during scaling.
        // Hence, no offset is necessary.
        const markerTop = overlayPositionForPercents[percent]! - MARKER_HEIGHT / 2;
        const scaledOffsets = createScaledOffsets(MARKER_HEIGHT, markerWidth, inverseScales);

        // Place the marker at the right end of the screen.
        const markerLeft =
          pageBounds.right -
          pageBounds.left -
          markerWidth * inverseScales.x -
          scaledOffsets.x * inverseScales.x;

        return (
          <ScrollMapsMarker
            key={percent}
            circleColor={getScrollMapsColorForPageView(colorMap, getPageViewNumber(percent))}
            pageViews={pageViewText}
            totalPageViews={shortNumber(facetData.totalPageViews)}
            pageViewPercent={percent.toString()}
            inverseScales={inverseScales}
            markerBoxBounds={{
              top: markerTop,
              bottom:
                markerTop + MARKER_HEIGHT * inverseScales.y + scaledOffsets.y * inverseScales.y,
              left: markerLeft,
              right: markerLeft + markerWidth * inverseScales.x + scaledOffsets.x * inverseScales.x,
              height: MARKER_HEIGHT,
              width: markerWidth,
              zIndex: pageBounds.zIndex + zIndexOffset,
            }}
            tooltipPlacement="top"
          />
        );
      })}
    </>
  );
};
